import * as React from "react";
import api, { $ } from "kw/api";

type TUseUpdatePolicyProps = {
  policyId: string;
  riskData: TRiskData;
  autoRenewEnabled?: boolean;
};

type TUseUpdatePolicy = {
  updatePolicy: () => Promise<void>;
  loading: boolean;
};

export const useUpdatePolicy = (props: TUseUpdatePolicyProps): TUseUpdatePolicy => {
  const { policyId, riskData, autoRenewEnabled } = props;
  const [loading, setLoading] = React.useState(false);
  const riskDataJSON = JSON.stringify(riskData);

  const updatePolicy = async () => {
    setLoading(true);
    try {
      await api.mutation(
        {
          updatePolicy: [
            {
              input: $("input", "UpdatePolicyInput!"),
            },
            {
              autoRenewEnabled: true,
            },
          ],
        },
        {
          input: {
            policyId,
            riskDataJSON,
            autoRenewEnabled,
            flow: "na",
          },
        }
      );
    } finally {
      setLoading(false);
    }
  };

  return { updatePolicy, loading };
};

export function useConfirmPolicyUpdate({ policyId, startDate }) {
  const [, setPolicy] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const confirmPolicyUpdate = async (autoRenewEnabled) => {
    setLoading(true);
    try {
      const response = await api.mutation(getConfirmPolicyUpdateQuery(), {
        input: {
          policyId,
          startDate,
          autoRenewEnabled,
        },
      });
      const policyResponse = response?.confirmPolicyUpdate || {};
      setPolicy(policyResponse);
    } finally {
      setLoading(false);
    }
  };

  return { confirmPolicyUpdate, loading };
}

function getConfirmPolicyUpdateQuery() {
  return {
    confirmPolicyUpdate: [
      {
        input: $("input", "ConfirmPolicyUpdateInput!"),
      },
      {
        policyNumber: true,
        startDate: true,
        finishDate: true,
        quote: {
          periodicPrice: true,
        },
        riskProfile: {
          riskData: true,
        },
      },
    ],
  };
}

export function usePolicyUpdateSummary({ policyId }) {
  const [updateSummary, setUpdateSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    async function effect() {
      setLoading(true);
      try {
        const response = await api.mutation(getPolicyUpdateSummaryQuery(policyId));
        const policyResponse = response?.policyUpdateSummary || {};
        setUpdateSummary(policyResponse);
      } finally {
        setLoading(false);
      }
    }
    if (policyId) {
      effect();
    }
  }, [policyId]);

  return { updateSummary, loading };
}

function getPolicyUpdateSummaryQuery(policyId) {
  return {
    policyUpdateSummary: [
      {
        input: {
          policyId: policyId,
        },
      },
      {
        summary: true,
        proRataAmount: true,
        surcharge: true,
        amountDue: true,
        oldPeriodicPrice: true,
        newPeriodicPrice: true,
        oldPaymentFrequency: true,
        newPaymentFrequency: true,
        newStartDate: true,
        newFinishDate: true,
      },
    ],
  };
}
